import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatCurrency, formatNumber } from '@/lib/campaign-dashboard'
import { managerialIsoDate } from '@/lib/managerial-calendar'
import {
  formatRevenueAxis,
  formatSalesEvolutionTick,
  formatSoldNumbersAxis,
  SALES_EVOLUTION_PERIODS,
  type SalesEvolutionPeriodId,
  type SalesEvolutionPoint,
} from '../../lib/sales-evolution'
import { KPI_LOADING_DISPLAY } from '../../lib/managerial-kpis'
import styles from './SalesEvolutionChart.module.css'

interface SalesEvolutionChartProps {
  points: SalesEvolutionPoint[]
  period: SalesEvolutionPeriodId
  customFrom: string
  customTo: string
  isLoading: boolean
  isBusy?: boolean
  hasData?: boolean
  onPeriodChange: (period: SalesEvolutionPeriodId) => void
  onCustomFromChange: (value: string) => void
  onCustomToChange: (value: string) => void
}

export function SalesEvolutionChart({
  points,
  period,
  customFrom,
  customTo,
  isLoading,
  isBusy = false,
  hasData = true,
  onPeriodChange,
  onCustomFromChange,
  onCustomToChange,
}: SalesEvolutionChartProps) {
  const showEmptyRecorte = hasData && points.length === 0
  const maxSelectableDate = managerialIsoDate()

  return (
    <section
      className={styles.card}
      aria-labelledby="sales-evolution-title"
      aria-busy={isLoading || isBusy}
    >
      <h2 id="sales-evolution-title" className={styles.title}>
        Evolução de vendas e GMV
      </h2>

      <div className={styles.pills} role="group" aria-label="Período do gráfico">
        {SALES_EVOLUTION_PERIODS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`${styles.pill} ${period === item.id ? styles.pillActive : ''}`}
            aria-pressed={period === item.id}
            onClick={() => onPeriodChange(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {period === 'custom' ? (
        <div className={styles.customRange}>
          <label className={styles.dateField} htmlFor="sales-evolution-date-from">
            Data inicial
            <input
              id="sales-evolution-date-from"
              type="date"
              className={styles.dateInput}
              value={customFrom}
              max={maxSelectableDate}
              onChange={(event) => onCustomFromChange(event.target.value)}
            />
          </label>
          <label className={styles.dateField} htmlFor="sales-evolution-date-to">
            Data final
            <input
              id="sales-evolution-date-to"
              type="date"
              className={styles.dateInput}
              value={customTo}
              max={maxSelectableDate}
              onChange={(event) => onCustomToChange(event.target.value)}
            />
          </label>
        </div>
      ) : null}

      <ul className={styles.legend} aria-label="Legenda do gráfico">
        <li className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendSold}`} />
          Números vendidos
        </li>
        <li className={styles.legendItem}>
          <span className={`${styles.legendDot} ${styles.legendRevenue}`} />
          GMV (R$)
        </li>
      </ul>

      <div className={styles.chartArea}>
        {isLoading ? (
          <p className={styles.empty}>Carregando evolução...</p>
        ) : showEmptyRecorte ? (
          <p className={styles.empty}>Nenhum dado no período selecionado.</p>
        ) : points.length === 0 ? (
          <p className={styles.empty}>{KPI_LOADING_DISPLAY}</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={points}
              barCategoryGap="46%"
              margin={{ top: 4, right: 4, left: -8, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(148, 163, 184, 0.22)"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tickFormatter={formatSalesEvolutionTick}
                tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                minTickGap={28}
                height={22}
              />
              <YAxis
                yAxisId="soldNumbers"
                tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
                tickFormatter={formatSoldNumbersAxis}
                axisLine={false}
                tickLine={false}
                width={34}
              />
              <YAxis
                yAxisId="revenue"
                orientation="right"
                tick={{ fill: 'var(--color-text-muted)', fontSize: 10 }}
                tickFormatter={formatRevenueAxis}
                axisLine={false}
                tickLine={false}
                width={58}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 10,
                  border: '1px solid var(--color-border-subtle)',
                  boxShadow: '0 8px 20px rgba(94, 23, 235, 0.08)',
                  fontSize: 12,
                }}
                formatter={(value, name) => {
                  const numericValue =
                    typeof value === 'number' ? value : Number(value ?? 0)
                  if (name === 'GMV (R$)') return [formatCurrency(numericValue), name]
                  return [formatNumber(numericValue), name]
                }}
                labelFormatter={(label) =>
                  typeof label === 'string'
                    ? formatSalesEvolutionTick(label)
                    : String(label ?? '')
                }
              />
              <Bar
                yAxisId="soldNumbers"
                dataKey="soldNumbers"
                name="Números vendidos"
                fill="var(--dash-chart-sold, #8b5cf6)"
                radius={[4, 4, 0, 0]}
                barSize={6}
                maxBarSize={8}
              />
              <Line
                yAxisId="revenue"
                type="monotone"
                dataKey="revenue"
                name="GMV (R$)"
                stroke="var(--dash-chart-revenue, #10b981)"
                strokeWidth={2}
                dot={{ r: 2.25, fill: '#10b981', stroke: '#fff', strokeWidth: 1.5 }}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  )
}
